import { useMemo, useState, type FormEvent } from 'react';
import type { CreateCertificateRequestBody, Framework, VendorDeviceRef } from '@mvcp/contracts';
import { requestCertificate } from '../api';
import { deviceLabel } from './device-label';
import { proposedOptionSuffix } from './proposed-tag';

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Compliance-Manager "Request certificate from vendor" form (Story 6.9, FR-12). The
 * request-by-email twin of {@link CreateCertificateForm}: same controlled-form pattern
 * and the SAME scope→subject coherence — a company-scoped framework targets the Vendor,
 * a device-scoped one targets a Device (mirrors `isDeviceScoped` gating). It records the
 * Story 3.4 `certificate_request` AND mints a passwordless magic-link upload token, then
 * "emails" (dev: logs) the link to the Vendor address entered here. The subject is
 * DERIVED from the cockpit context (`vendorId` + the in-scope `devices`), never
 * free-typed. On success a confirmation names the email; a 400/404/409 surfaces inline.
 */
export function RequestCertificateForm({
  frameworks,
  vendorId,
  devices = [],
  onDone,
  onCancel,
}: {
  /** Applicable frameworks for this subject. */
  frameworks: Framework[];
  /** The Vendor subject id — target of a company-scoped request. */
  vendorId: string;
  /** Device-subject options for a device-scoped framework (id + model label). */
  devices?: VendorDeviceRef[];
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const defaultDeviceId = devices.length === 1 ? devices[0]!.id : '';
  const [frameworkId, setFrameworkId] = useState('');
  const [deviceId, setDeviceId] = useState(defaultDeviceId);
  const [vendorEmail, setVendorEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const selected = useMemo(
    () => frameworks.find((f) => f.id === frameworkId),
    [frameworks, frameworkId],
  );
  const isDeviceScoped = selected?.scope === 'device';

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!selected) {
      setError('Select a framework to request.');
      return;
    }
    const subjectType: 'vendor' | 'device' = isDeviceScoped ? 'device' : 'vendor';
    if (subjectType === 'device' && !deviceId) {
      setError('Select the device this certificate applies to.');
      return;
    }
    const email = vendorEmail.trim();
    if (!email) {
      setError("Enter the vendor's email address.");
      return;
    }
    const subjectId = subjectType === 'device' ? deviceId : vendorId;

    const body: CreateCertificateRequestBody = {
      frameworkId: selected.id,
      subjectType,
      subjectId,
      vendorEmail: email,
    };

    setSubmitting(true);
    try {
      await requestCertificate(body);
      // Keep the form mounted so the confirmation (naming the email) is visible; the
      // parent collapses only when the Manager clicks Done below.
      setSentTo(email);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't send the request. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Post-success confirmation naming the email (the form itself collapses via onDone,
  // but the parent may keep this mounted briefly — show the confirmation if we have it).
  if (sentTo) {
    return (
      <div role="status" aria-live="polite" className="stack" style={{ gap: 6 }}>
        <span className="status-pill" data-tone="ok">
          <span className="status-pill__dot" aria-hidden="true" />
          A secure upload link was emailed to {sentTo}.
        </span>
        <span className="muted" style={{ fontSize: 12 }}>
          In local dev the link is printed in the API log (no mail is sent).
        </span>
        <button
          type="button"
          className="icon-button"
          onClick={onDone}
          style={{ alignSelf: 'flex-start' }}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      className="stack"
      aria-label="Request a certificate from the vendor"
      style={{ gap: 10 }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
        <label className="stack" style={{ gap: 4, flex: '1 1 240px' }}>
          <span className="field-label">Framework</span>
          <select
            aria-label="Framework"
            value={frameworkId}
            onChange={(e) => {
              setFrameworkId(e.target.value);
              setDeviceId(defaultDeviceId);
              setError(null);
            }}
            style={fieldStyle}
          >
            <option value="">Select a framework…</option>
            {frameworks.map((f) => (
              <option key={f.id} value={f.id}>
                {f.code} — {f.name}
              </option>
            ))}
          </select>
        </label>

        {isDeviceScoped && (
          <label className="stack" style={{ gap: 4, flex: '1 1 200px' }}>
            <span className="field-label">Device</span>
            <select
              aria-label="Device"
              value={deviceId}
              onChange={(e) => {
                setDeviceId(e.target.value);
                setError(null);
              }}
              style={fieldStyle}
            >
              <option value="">Select a device…</option>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {/* Story 8.5 (AD-25): a device SUBJECT is model + revision, composed by
                      the one web owner. Two revisions of one model are now
                      distinguishable at the point coverage is asserted; a version-less
                      device renders the bare model with no stray artifact.

                      Story 8.9: a device that is still a tenant PROPOSAL says so, in the
                      marker owner's text form — an `<option>` cannot host the pill, and
                      the status may not enter `deviceLabel` (AD-25 clause 3). A curated
                      device renders exactly the text it did before, with no trailing
                      separator. */}
                  {deviceLabel(d)}
                  {proposedOptionSuffix(d.proposed)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
          <span className="field-label">Vendor email</span>
          <input
            type="email"
            aria-label="Vendor email"
            placeholder="vendor@example.com"
            value={vendorEmail}
            onChange={(e) => {
              setVendorEmail(e.target.value);
              setError(null);
            }}
            style={fieldStyle}
          />
        </label>

        <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send request'}
        </button>
        <button type="button" className="icon-button" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
      </div>
      <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
        {error && (
          <span className="status-pill" data-tone="bad">
            <span className="status-pill__dot" aria-hidden="true" />
            {error}
          </span>
        )}
      </div>
    </form>
  );
}
